import { formatDate } from '@core/utils/filter'

export const DEFAULT_CUSTOM_FIELD_SECTION = 'Inne'

export const normalizeCustomField = (customField) => {
  if (!customField) return customField

  return {
    ...customField,
    fieldType: customField.fieldType || customField.field_type || 'text',
    showInSummary: customField.showInSummary ?? customField.show_in_summary ?? false,
    scope: customField.scope || 'both',
    section: customField.section || '',
    options: customField.options || [],
  }
}

export const normalizeCustomFields = (customFields = []) => {
  return (customFields || []).map(normalizeCustomField)
}

export const getCustomFieldsForCandidate = (customFields = []) => {
  return normalizeCustomFields(customFields).filter(cf => cf.scope !== 'employee')
}

export const getCustomFieldsForEmployee = (customFields = []) => {
  return normalizeCustomFields(customFields).filter(cf => cf.scope !== 'candidate')
}

export const initCustomFieldsForForm = (customFields = [], { forEntity = 'candidate' } = {}) => {
  const filtered = forEntity === 'employee'
    ? getCustomFieldsForEmployee(customFields)
    : getCustomFieldsForCandidate(customFields)

  return filtered.map((customField) => ({
    ...customField,
    field: customField.field ?? (customField.fieldType === 'boolean' ? 'false' : ''),
  }))
}

const getSectionKey = (section) => {
  const normalized = (section || '').trim().toLowerCase()
  return normalized || DEFAULT_CUSTOM_FIELD_SECTION.toLowerCase()
}

export const getPinnedDetailsFields = (customFields = [], { excludeScope } = {}) => {
  return normalizeCustomFields(customFields).filter(cf => {
    if (!cf.showInSummary) return false
    if (excludeScope && cf.scope === excludeScope) return false
    const section = getSectionKey(cf.section)
    return section === DEFAULT_CUSTOM_FIELD_SECTION.toLowerCase()
  })
}

export const getPinnedCustomFieldsBySection = (customFields = [], { excludeScope } = {}) => {
  const pinned = normalizeCustomFields(customFields).filter(cf => {
    if (!cf.showInSummary) return false
    if (excludeScope && cf.scope === excludeScope) return false
    const section = getSectionKey(cf.section)
    return section !== DEFAULT_CUSTOM_FIELD_SECTION.toLowerCase()
  })

  if (!pinned.length) return []

  const sectionMap = {}
  pinned.forEach(cf => {
    const section = cf.section || DEFAULT_CUSTOM_FIELD_SECTION
    if (!sectionMap[section]) sectionMap[section] = []
    sectionMap[section].push(cf)
  })

  return Object.entries(sectionMap).map(([name, fields]) => ({ name, fields }))
}

export const safeFormatDate = (value) => {
  if (!value) return '-/-'
  try {
    const formatted = formatDate(value)
    return formatted || '-/-'
  } catch (e) {
    return '-/-'
  }
}

export const isParsableDate = (value) => {
  if (!value) return false
  const date = new Date(value)

  return !Number.isNaN(date.getTime())
}

// Returns a human-readable representation of a custom field value for read-only
// summaries. Never hides a non-empty value: when the stored string does not
// match the current field type (e.g. legacy data after a type change), the raw
// value is shown instead of an empty cell.
export const formatCustomFieldDisplayValue = (customField, t) => {
  if (!customField) return '-/-'
  const value = customField.field
  const type = customField.fieldType

  if (type === 'boolean') {
    if (value === 'true') return t('yes')
    if (value === 'false') return t('no')
    if (!value) return '-/-'

    return value
  }

  if (type === 'date') {
    if (!value) return '-/-'

    return isParsableDate(value) ? safeFormatDate(value) : value
  }

  return value || '-/-'
}
